/**
 * Order filters: simple search input with loading indicator.
 * Status filtering is handled by OrderStatusSlider.
 */

import { useState, useEffect, useRef } from "react";
import { IconSearch, IconRefresh } from "../../../../components/Icons.tsx";

export interface OrderFiltersProps {
  /** Current search query */
  searchQuery: string;
  /** Callback when search changes */
  onSearchChange: (query: string) => void;
  /** Is loading/searching */
  isLoading?: boolean;
  /** Callback for refresh */
  onRefresh?: () => void;
  /** Show search input */
  showSearch?: boolean;
  /** Placeholder for search */
  searchPlaceholder?: string;
}

export function OrderFilters({
  searchQuery,
  onSearchChange,
  isLoading = false,
  onRefresh,
  showSearch = true,
  searchPlaceholder = "Поиск по заказам...",
}: OrderFiltersProps) {
  // Debounced loading state to avoid flickering
  const [showSpinner, setShowSpinner] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (isLoading && searchQuery.length > 0) {
      // Show spinner immediately
      clearTimeout(hideTimeoutRef.current);
      setShowSpinner(true);
    } else if (showSpinner) {
      // Hide spinner with small delay to avoid flicker
      hideTimeoutRef.current = setTimeout(() => {
        setShowSpinner(false);
      }, 150);
    }

    return () => clearTimeout(hideTimeoutRef.current);
  }, [isLoading, searchQuery.length, showSpinner]);

  if (!showSearch) return null;

  return (
    <div className="mb-3">
      <div className="relative">
        {/* Search icon or spinner */}
        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4">
          {showSpinner ? (
            <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin" />
          ) : (
            <IconSearch className="w-4 h-4 text-slate-400" />
          )}
        </div>

        <input
          type="search"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-slate-100 border-0 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/20"
          autoComplete="off"
        />

        {/* Right side: clear button or refresh */}
        {searchQuery ? (
          <button
            type="button"
            onClick={() => onSearchChange("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-300 text-slate-600 flex items-center justify-center text-sm leading-none active:opacity-80"
            aria-label="Очистить"
          >
            ×
          </button>
        ) : onRefresh ? (
          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-slate-400 active:opacity-70 disabled:opacity-50"
            aria-label="Обновить"
          >
            <IconRefresh
              className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
            />
          </button>
        ) : null}
      </div>
    </div>
  );
}
